import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useMemo } from 'react';
import { Platform, Pressable, ScrollView, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type {
  AdminReservation,
  Expense,
  OwnerCancellation,
  Reservation,
} from '@/api';
import { useExportPayout } from '@/api/partners/payouts/use-export-payout';
import { usePayout } from '@/api/partners/payouts/use-payout';
import { formatInUTC, formatNumber, isDesktop, useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Card, showErrorMessage, Text } from '@/ui';
import DataTable from '@/ui/data-table';
import { InputMulti } from '@/ui/input-multi';
import { Loader } from '@/ui/loader';

import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

export default function Payout() {
  const { styles, theme } = useStyles(stylesheet);
  const local = useLocalSearchParams();
  const payoutId = typeof local.id === 'string' ? local.id : undefined;
  const router = useRouter();
  const status = useAuth.use.status();
  const { mutate: exportPayout, isPending: isExportingPayout } =
    useExportPayout();

  const { data, isPending, refetch } = usePayout({
    variables: { id: payoutId ?? '' },
    enabled: Boolean(payoutId),
  });

  useEffect(() => {
    if (status === 'signIn') {
      refetch();
    }
  }, [status]);

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Payout',
    });
  }, []);

  const renderAmountCell = (amount: number) => (
    <Text type="caption1">${amount.toFixed(2)}</Text>
  );

  const dailyRentalRows = useMemo(() => {
    if (!data) return [];

    return data.reservations
      .filter((trip: AdminReservation) => trip.subscription === null)
      .map((trip: AdminReservation) => ({
        key: `daily-${trip.id}`,
        'reservation-id': (
          <Pressable
            hitSlop={8}
            onPress={() => {
              router.push(`/partner/reservations/${trip.id}`);
            }}
          >
            <Text
              type="bodyRegular"
              style={{ textDecorationLine: 'underline' }}
            >
              {trip.id}
            </Text>
          </Pressable>
        ),
        car: (
          <Pressable
            hitSlop={8}
            onPress={() => {
              router.push(`/partner/cars/${trip.car.id}`);
            }}
          >
            <Text
              type="bodyRegular"
              style={{ textDecorationLine: 'underline' }}
            >
              {trip.car.licensePlate}
            </Text>
          </Pressable>
        ),
        'start-time': (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {formatInUTC(trip.startTime, "MMM d, yyyy | h:mm a 'UTC'")}
          </Text>
        ),
        'end-time': (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {formatInUTC(trip.endTime, "MMM d, yyyy | h:mm a 'UTC'")}
          </Text>
        ),
        'start-location': (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {trip.customPickupLocation || trip.pickupLocation.address}
          </Text>
        ),
        days: (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {trip.numDays}
          </Text>
        ),
        'daily-price': (
          <View>
            {trip.reservationRates.map((rate) => (
              <Text key={rate.id} type="caption1">
                {`$${rate.dailyRate.toFixed(2)} x ${rate.numDays} days`}
              </Text>
            ))}
          </View>
        ),
        'weekly-discount': (
          <Text
            type="caption1"
            style={{ color: theme.colors.chip.brand.color }}
          >
            {trip.weeklyDiscount > 0
              ? `-$${trip.weeklyDiscount.toFixed(2)}`
              : '$0.00'}
          </Text>
        ),
        'monthly-discount': (
          <Text
            type="caption1"
            style={{ color: theme.colors.chip.brand.color }}
          >
            {trip.monthlyDiscount > 0
              ? `-$${trip.monthlyDiscount.toFixed(2)}`
              : '$0.00'}
          </Text>
        ),
        'your-earnings': (
          <Text type="caption1">{`${trip.hostEarningsBreakdown?.ownerEarningsPercent?.toFixed(
            2
          )}%`}</Text>
        ),
        'extra-miles': (
          <Text type="caption1">${(trip.extraMiles * 0.7).toFixed(2)}</Text>
        ),
        supercharging: renderAmountCell(trip.resolvedChargingAmount),
        tolls: renderAmountCell(trip.resolvedTollsAmount),
        'owner-discount': (
          <Text
            type="caption1"
            style={{ color: theme.colors.chip.brand.color }}
          >
            {trip.ownerDiscount
              ? `-$${trip.ownerDiscount.toFixed(2)}`
              : '$0.00'}
          </Text>
        ),
        delivery: (
          <Text type="caption1">
            {trip.ownerDeliveryPayout === 0
              ? '$0.00'
              : `$${trip.ownerDeliveryPayout.toFixed(2)}`}
          </Text>
        ),
        notes: (
          <View style={{ width: 150 }}>
            <Text
              type="caption1"
              style={{
                color: theme.colors.text.secondary,
              }}
            >
              {trip.hostNotes}
            </Text>
          </View>
        ),
        total: (
          <Text type="caption1" style={{ color: theme.colors.success }}>
            {trip.ownerEarnings ? `$${trip.ownerEarnings.toFixed(2)}` : '$0.00'}
          </Text>
        ),
      }));
  }, [data, router, theme]);

  const dailyRentalFooterValues = useMemo(() => {
    if (!data) return null;

    let totalDays = 0;
    let totalWeeklyDiscount = 0;
    let totalMonthlyDiscount = 0;
    let totalExtraMiles = 0;
    let totalSupercharging = 0;
    let totalTolls = 0;
    let totalOwnerDiscount = 0;
    let totalDelivery = 0;
    let grandTotal = 0;
    data.reservations
      .filter((trip: AdminReservation) => trip.subscription === null)
      .forEach((trip: AdminReservation) => {
        totalDays += trip.numDays;
        totalWeeklyDiscount += trip.weeklyDiscount;
        totalMonthlyDiscount += trip.monthlyDiscount;
        totalExtraMiles += trip.extraMiles * 0.7;
        totalSupercharging += trip.resolvedChargingAmount;
        totalTolls += trip.resolvedTollsAmount;
        totalOwnerDiscount += trip.ownerDiscount;
        totalDelivery += trip.ownerDeliveryPayout;
        grandTotal += trip.ownerEarnings;
      });

    return {
      'reservation-id': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          TOTAL
        </Text>
      ),
      days: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {totalDays}
        </Text>
      ),
      'weekly-discount': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {totalWeeklyDiscount === 0
            ? '$0.00'
            : `-$${totalWeeklyDiscount.toFixed(2)}`}
        </Text>
      ),
      'monthly-discount': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {totalMonthlyDiscount === 0
            ? '$0.00'
            : `-$${totalMonthlyDiscount.toFixed(2)}`}
        </Text>
      ),
      'extra-miles': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {totalExtraMiles.toFixed(2)}
        </Text>
      ),
      supercharging: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalSupercharging.toFixed(2)}
        </Text>
      ),
      tolls: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalTolls.toFixed(2)}
        </Text>
      ),
      'owner-discount': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {totalOwnerDiscount === 0
            ? '$0.00'
            : `-$${totalOwnerDiscount.toFixed(2)}`}
        </Text>
      ),
      delivery: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalDelivery.toFixed(2)}
        </Text>
      ),
      total: (
        <Text type="caption1" style={{ color: theme.colors.success }}>
          ${grandTotal.toFixed(2)}
        </Text>
      ),
    };
  }, [data, theme]);

  const subscriptionRows = useMemo(() => {
    if (!data) return [];

    return data.reservations
      .filter((trip: AdminReservation) => trip.subscription !== null)
      .map((trip: AdminReservation) => ({
        key: `subscription-${trip.id}`,
        'reservation-id': (
          <Pressable
            hitSlop={8}
            onPress={() => {
              router.push(`/partner/reservations/${trip.id}`);
            }}
          >
            <Text
              type="bodyRegular"
              style={{ textDecorationLine: 'underline' }}
            >
              {trip.id}
            </Text>
          </Pressable>
        ),
        car: (
          <Pressable
            hitSlop={8}
            onPress={() => {
              router.push(`/partner/cars/${trip.car.id}`);
            }}
          >
            <Text
              type="bodyRegular"
              style={{ textDecorationLine: 'underline' }}
            >
              {trip.car.licensePlate}
            </Text>
          </Pressable>
        ),
        'start-time': (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {formatInUTC(trip.startTime, "MMM d, yyyy | h:mm a 'UTC'")}
          </Text>
        ),
        'end-time': (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {formatInUTC(trip.endTime, "MMM d, yyyy | h:mm a 'UTC'")}
          </Text>
        ),
        'start-location': (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {trip.customPickupLocation || trip.pickupLocation.address}
          </Text>
        ),
        period: (
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            {trip.subscription.renewalPeriod === '6_months'
              ? 'monthly'
              : trip.subscription.renewalPeriod}
          </Text>
        ),
        'mileage-tier': (
          <Text type="caption1">{trip.subscription.mileageTier}</Text>
        ),
        'tier-cost': (
          <Text type="caption1">${trip.subscription.tierCost.toFixed(2)}</Text>
        ),
        supercharging: renderAmountCell(trip.resolvedChargingAmount),
        tolls: renderAmountCell(trip.resolvedTollsAmount),
        'owner-discount': (
          <Text
            type="caption1"
            style={{ color: theme.colors.chip.brand.color }}
          >
            {trip.ownerDiscount
              ? `-$${trip.ownerDiscount.toFixed(2)}`
              : '$0.00'}
          </Text>
        ),
        delivery: (
          <Text type="caption1">
            {trip.ownerDeliveryPayout === 0
              ? '$0.00'
              : `$${trip.ownerDeliveryPayout.toFixed(2)}`}
          </Text>
        ),
        notes: (
          <View style={{ width: 150 }}>
            <Text
              type="caption1"
              style={{
                color: theme.colors.text.secondary,
              }}
            >
              {trip.hostNotes}
            </Text>
          </View>
        ),
        total: (
          <Text type="caption1" style={{ color: theme.colors.success }}>
            {trip.ownerEarnings ? `$${trip.ownerEarnings.toFixed(2)}` : '$0.00'}
          </Text>
        ),
      }));
  }, [data, router, theme]);

  const subscriptionFooterValues = useMemo(() => {
    if (!data) return null;

    let totalTierCost = 0;
    let totalSupercharging = 0;
    let totalTolls = 0;
    let totalOwnerDiscount = 0;
    let totalDelivery = 0;
    let grandTotal = 0;
    data.reservations
      .filter((trip: AdminReservation) => trip.subscription !== null)
      .forEach((trip: AdminReservation) => {
        totalTierCost += trip.subscription.tierCost;
        totalSupercharging += trip.resolvedChargingAmount;
        totalTolls += trip.resolvedTollsAmount;
        totalOwnerDiscount += trip.ownerDiscount;
        totalDelivery += trip.ownerDeliveryPayout;
        grandTotal += trip.ownerEarnings;
      });

    return {
      'reservation-id': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          TOTAL
        </Text>
      ),
      'tier-cost': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalTierCost.toFixed(2)}
        </Text>
      ),
      supercharging: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalSupercharging.toFixed(2)}
        </Text>
      ),
      tolls: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalTolls.toFixed(2)}
        </Text>
      ),
      'owner-discount': (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          {totalOwnerDiscount === 0
            ? '$0.00'
            : `-$${totalOwnerDiscount.toFixed(2)}`}
        </Text>
      ),
      delivery: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          ${totalDelivery.toFixed(2)}
        </Text>
      ),
      total: (
        <Text type="caption1" style={{ color: theme.colors.success }}>
          ${grandTotal.toFixed(2)}
        </Text>
      ),
    };
  }, [data, theme]);

  const adjustmentRows = useMemo(() => {
    if (!data) return [];

    const expenseRows = data.expenses.map((expense: Expense) => {
      return {
        key: `expense-${expense.id}`,
        id: expense.id,
        adjustments: (
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {expense.title}
          </Text>
        ),
        'quantity-days-total': (
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {expense.quantity}
          </Text>
        ),
        'price-per-unit': (
          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            {expense.amount > 0 ? '-' : ''}$
            {Math.abs(expense.amount).toFixed(2)}
          </Text>
        ),
        subtotal: (
          <Text
            type="bodyRegular"
            style={{
              color:
                expense.totalCost > 0
                  ? theme.colors.error
                  : theme.colors.success,
            }}
          >
            {expense.amount > 0 ? '-' : ''}$
            {Math.abs(expense.totalCost).toFixed(2)}
          </Text>
        ),
      };
    });

    const cancellationRows = data.ownerCancellations.map(
      (cancellation: OwnerCancellation) => {
        return {
          key: `cancellation-${cancellation.id}`,
          id: cancellation.id,
          adjustments: (
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Owner Cancellation TR-{cancellation.reservationId}
            </Text>
          ),
          'quantity-days-total': (
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              1
            </Text>
          ),
          'price-per-unit': (
            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              -${(cancellation.penaltyAmount / 100).toFixed(2)}
            </Text>
          ),
          subtotal: (
            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.error,
              }}
            >
              -${(cancellation.penaltyAmount / 100).toFixed(2)}
            </Text>
          ),
        };
      }
    );

    return expenseRows.concat(cancellationRows);
  }, [data, theme]);

  const adjustmentFooterValues = useMemo(() => {
    if (!data) return null;

    let grandTotal = 0;
    data.expenses.forEach((expense: Expense) => {
      grandTotal += expense.totalCost * -1;
    });
    data.ownerCancellations.forEach((cancellation) => {
      grandTotal -= cancellation.penaltyAmount / 100;
    });

    return {
      adjustments: (
        <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
          TOTAL
        </Text>
      ),
      subtotal: (
        <Text
          type="caption1"
          style={{
            color: grandTotal > 0 ? theme.colors.success : theme.colors.error,
          }}
        >
          {grandTotal > 0 ? '' : '-'}${Math.abs(grandTotal).toFixed(2)}
        </Text>
      ),
    };
  }, [data, theme]);

  const totalRentals = useMemo(() => {
    if (!data) return 0;

    return data.reservations.filter(
      (trip: Reservation) => trip.subscription === null
    ).length;
  }, [data]);

  const totalDailyRentalDays = useMemo(() => {
    if (!data) return 0;

    return data.reservations
      .filter((trip: Reservation) => trip.subscription === null)
      .reduce((acc: number, trip: Reservation) => acc + trip.numDays, 0);
  }, [data]);

  const totalDailyEarnings = useMemo(() => {
    if (!data) return 0;

    return data.reservations
      .filter((trip: Reservation) => trip.subscription === null)
      .reduce((acc: number, trip: Reservation) => {
        trip.ownerEarnings ? (acc += trip.ownerEarnings) : (acc += 0);
        return acc;
      }, 0);
  }, [data]);

  const totalSubscriptionRentals = useMemo(() => {
    if (!data) return 0;

    return data.reservations.filter(
      (trip: Reservation) => trip.subscription !== null
    ).length;
  }, [data]);

  const totalSubscriptionRentalDays = useMemo(() => {
    if (!data) return 0;

    return data.reservations
      .filter((trip: Reservation) => trip.subscription !== null)
      .reduce((acc: number, trip: Reservation) => acc + trip.numDays, 0);
  }, [data]);

  const totalSubscriptionEarnings = useMemo(() => {
    if (!data) return 0;

    return data.reservations
      .filter((trip: Reservation) => trip.subscription !== null)
      .reduce((acc: number, trip: Reservation) => {
        trip.ownerEarnings ? (acc += trip.ownerEarnings) : (acc += 0);
        return acc;
      }, 0);
  }, [data]);

  const totalAdjustmentAmount = useMemo(() => {
    if (!data) return 0;

    let total = 0;
    data.expenses.forEach((expense: Expense) => {
      total += expense.totalCost * -1;
    });
    data.ownerCancellations.forEach((cancellation) => {
      total -= cancellation.penaltyAmount / 100;
    });

    return total;
  }, [data]);

  const handleExportPayout = () => {
    if (Platform.OS !== 'web' || !payoutId) {
      return;
    }

    exportPayout(
      { id: payoutId },
      {
        onSuccess: (blob) => {
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = url;
          link.download = `payout_${payoutId}_breakdown.zip`;
          document.body.appendChild(link);
          link.click();
          link.remove();

          setTimeout(() => URL.revokeObjectURL(url), 0);
        },
        onError: (error) => {
          showErrorMessage(error.message);
        },
      }
    );
  };

  if (isPending) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (!data) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
        }}
      />
    );
  }

  if (isDesktop()) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
        }}
      >
        <ScrollView
          style={{ flex: 1, width: '100%' }}
          contentContainerStyle={styles.container}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.nav}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.navigate('/partner/payouts');
              }}
            />

            <Text type="title1">
              Period: {formatInUTC(data.startTime, 'MMM d yyyy')} -{' '}
              {formatInUTC(data.endTime, 'MMM d yyyy')}
            </Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Total net earnings: ${formatNumber(data.earningsMinusExpenses)}
            </Text>
          </View>

          <View
            style={{
              flex: 1,
              gap: 24,
            }}
          >
            <View style={{ gap: 48 }}>
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                }}
              >
                <Text type="title2">Daily Rentals</Text>

                <View style={{ width: 360, alignItems: 'flex-end', gap: 8 }}>
                  <View style={{ width: 180 }}>
                    <Button
                      text="Download CSV"
                      type="secondarySmall"
                      onPress={handleExportPayout}
                      loading={isExportingPayout}
                      disabled={isExportingPayout}
                    />
                  </View>

                  <Text
                    type="caption2"
                    style={{
                      color: theme.colors.text.secondary,
                      textAlign: 'right',
                    }}
                  >
                    This CSV is for your records and internal tracking. Earnings
                    may not match your dashboard and should not be used for tax
                    reporting.
                  </Text>
                </View>
              </View>

              <DataTable
                columns={[
                  {
                    key: 'reservation-id',
                    title: 'Reservation ID',
                    style: { minWidth: 110 },
                  },
                  { key: 'car', title: 'CAR', style: { minWidth: 130 } },
                  {
                    key: 'start-time',
                    title: 'START TIME',
                    style: { minWidth: 190 },
                  },
                  {
                    key: 'end-time',
                    title: 'END TIME',
                    style: { minWidth: 190 },
                  },
                  {
                    key: 'start-location',
                    title: 'START LOCATION',
                    style: { minWidth: 200 },
                  },
                  { key: 'days', title: 'DAYS', style: { minWidth: 70 } },
                  {
                    key: 'daily-price',
                    title: 'DAILY PRICE',
                    style: { minWidth: 150 },
                  },
                  {
                    key: 'weekly-discount',
                    title: 'WEEKLY DISCOUNT',
                    style: { minWidth: 140 },
                  },
                  {
                    key: 'monthly-discount',
                    title: 'MONTHLY DISCOUNT',
                    style: { minWidth: 150 },
                  },
                  {
                    key: 'your-earnings',
                    title: 'YOUR EARNINGS',
                    style: { minWidth: 130 },
                  },
                  {
                    key: 'extra-miles',
                    title: 'EXTRA MILES',
                    style: { minWidth: 120 },
                  },
                  {
                    key: 'supercharging',
                    title: 'SUPERCHARGING',
                    style: { minWidth: 130 },
                  },
                  { key: 'tolls', title: 'TOLLS', style: { minWidth: 110 } },
                  {
                    key: 'owner-discount',
                    title: 'OWNER DISCOUNT',
                    style: { minWidth: 140 },
                  },
                  {
                    key: 'delivery',
                    title: 'DELIVERY',
                    style: { minWidth: 110 },
                  },
                  { key: 'notes', title: 'NOTES', style: { minWidth: 160 } },
                  { key: 'total', title: 'TOTAL', style: { minWidth: 110 } },
                ]}
                rows={dailyRentalRows}
                footerValues={dailyRentalFooterValues}
              />
            </View>

            <View style={{ gap: 16 }}>
              <Text type="title2">Subscriptions</Text>

              <DataTable
                columns={[
                  {
                    key: 'reservation-id',
                    title: 'Reservation ID',
                    style: { minWidth: 110 },
                  },
                  { key: 'car', title: 'CAR', style: { minWidth: 130 } },
                  {
                    key: 'start-time',
                    title: 'START TIME',
                    style: { minWidth: 190 },
                  },
                  {
                    key: 'end-time',
                    title: 'END TIME',
                    style: { minWidth: 190 },
                  },
                  {
                    key: 'start-location',
                    title: 'START LOCATION',
                    style: { minWidth: 200 },
                  },
                  { key: 'period', title: 'PERIOD', style: { minWidth: 130 } },
                  {
                    key: 'mileage-tier',
                    title: 'MILEAGE TIER',
                    style: { minWidth: 140 },
                  },
                  {
                    key: 'tier-cost',
                    title: 'TIER COST',
                    style: { minWidth: 120 },
                  },
                  {
                    key: 'supercharging',
                    title: 'SUPERCHARGING',
                    style: { minWidth: 130 },
                  },
                  { key: 'tolls', title: 'TOLLS', style: { minWidth: 110 } },
                  {
                    key: 'owner-discount',
                    title: 'OWNER DISCOUNT',
                    style: { minWidth: 140 },
                  },
                  {
                    key: 'delivery',
                    title: 'DELIVERY',
                    style: { minWidth: 110 },
                  },
                  { key: 'notes', title: 'NOTES', style: { minWidth: 160 } },
                  { key: 'total', title: 'TOTAL', style: { minWidth: 110 } },
                ]}
                rows={subscriptionRows}
                footerValues={subscriptionFooterValues}
              />
            </View>

            <View>
              <DataTable
                columns={[
                  {
                    key: 'adjustments',
                    title: 'ADJUSTMENTS',
                    style: { minWidth: 180 },
                  },
                  {
                    key: 'quantity-days-total',
                    title: 'QUANTITY/DAYS TOTAL',
                    style: { minWidth: 170 },
                  },
                  {
                    key: 'price-per-unit',
                    title: 'PRICE PER UNIT',
                    style: { minWidth: 140 },
                  },
                  {
                    key: 'subtotal',
                    title: 'SUBTOTAL',
                    style: { minWidth: 120 },
                  },
                ]}
                rows={adjustmentRows}
                footerValues={adjustmentFooterValues}
              />
            </View>

            <View style={{ width: 400 }}>
              <InputMulti
                text={data.memo}
                onChangeText={() => {}}
                placeholder="Notes"
                style={{ minHeight: 100 }}
                disabled
              />
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        paddingTop: 16,
      }}
    >
      <ScrollView
        style={{
          flex: 1,
        }}
        contentContainerStyle={{
          gap: 24,
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 24,
          paddingBottom: 30,
        }}
        showsVerticalScrollIndicator={false}
      >
        <View style={{ gap: 8 }}>
          <Text type="title2">
            Period: {formatInUTC(data.startTime, 'MMM d yyyy')} -{' '}
            {formatInUTC(data.endTime, 'MMM d yyyy')}
          </Text>

          <Text
            type="bodyRegular"
            style={{ color: theme.colors.text.secondary }}
          >
            Rentals that fall between{' '}
            {formatInUTC(data.startTime, 'MMM d, yyyy')} and{' '}
            {formatInUTC(data.endTime, 'MMM d, yyyy')} will be included in this
            payout. Rentals outside this will appear in the next payout.
          </Text>
        </View>

        <View style={{ gap: 16 }}>
          <Card>
            <View
              style={{
                padding: 16,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <Text type="title2">Daily Rentals</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                For a full breakdown, view on desktop
              </Text>
            </View>

            <View
              style={{
                padding: 16,
                gap: 8,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Rentals</Text>

                <Text type="bodyRegular">{totalRentals}</Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Rental Days</Text>

                <Text type="bodyRegular">{totalDailyRentalDays}</Text>
              </View>
            </View>

            <View
              style={{
                padding: 16,
                gap: 8,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Earnings</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.success }}
                >
                  ${formatNumber(totalDailyEarnings)}
                </Text>
              </View>
            </View>
          </Card>

          <Card>
            <View
              style={{
                padding: 16,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <Text type="title2">Subscriptions</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                For a full breakdown, view on desktop
              </Text>
            </View>

            <View
              style={{
                padding: 16,
                gap: 8,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Rentals</Text>

                <Text type="bodyRegular">{totalSubscriptionRentals}</Text>
              </View>

              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Rental Days</Text>

                <Text type="bodyRegular">{totalSubscriptionRentalDays}</Text>
              </View>
            </View>

            <View
              style={{
                padding: 16,
                gap: 8,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Earnings</Text>

                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.success }}
                >
                  ${formatNumber(totalSubscriptionEarnings)}
                </Text>
              </View>
            </View>
          </Card>

          <Card>
            <View
              style={{
                padding: 16,
                borderBottomWidth: 1,
                borderBottomColor: theme.colors.card.default.borderColor,
              }}
            >
              <Text type="title2">Adjustments</Text>

              <Text
                type="bodyRegular"
                style={{ color: theme.colors.text.secondary }}
              >
                For a full breakdown, view on desktop
              </Text>
            </View>

            <View
              style={{
                padding: 16,
                gap: 8,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <Text type="bodyRegular">Amount</Text>

                <Text
                  type="bodyRegular"
                  style={{
                    color:
                      totalAdjustmentAmount > 0
                        ? theme.colors.success
                        : theme.colors.error,
                  }}
                >
                  {totalAdjustmentAmount > 0 ? '' : '-'}$
                  {formatNumber(Math.abs(totalAdjustmentAmount))}
                </Text>
              </View>
            </View>
          </Card>
        </View>
      </ScrollView>

      <View
        style={{
          height: 75,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: theme.colors.card.default.backgroundColor,
          padding: 16,
        }}
      >
        <View>
          <Text type="bodyBold">Total net earnings</Text>
          <Text type="caption2">Including subscriptions</Text>
        </View>

        <Text type="title2">${formatNumber(data.earningsMinusExpenses)}</Text>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    borderRadius: 16,
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingTop: {
      xs: 16,
      md: 56,
    },
    paddingBottom: 48,
    maxHeight: {
      xs: '100%',
      md: 'auto',
    },
    gap: 56,
  },
  nav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    gap: 16,
  },
}));
